import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, Package, Briefcase, FolderOpen, Columns, Scale,
  History, Calculator, Tags, Shield, Eye,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { DEFAULT_ARTICLE_CONFIG, normalizeArticlesConfig } from './articlesConfigDefaults';
import { syncArticlesConfigToSettings } from './useArticlesConfig';

const NS = 'modules.article.pages.configPage';

function ConfigSection({ icon: Icon, title, hint, children }) {
  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <Icon className="h-6 w-6 shrink-0" />
        {title}
      </h2>
      {hint ? <p className="text-white/50 text-sm mb-4 text-right">{hint}</p> : <div className="mb-4" />}
      {children}
    </div>
  );
}

function ConfigCheckbox({ checked, onChange, label }) {
  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
      />
      <label className="ml-3 text-white/80">{label}</label>
    </div>
  );
}

function ConfigField({ label, hint, children }) {
  return (
    <div>
      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
      {children}
      {hint ? <p className="text-white/50 text-xs mt-1 text-right">{hint}</p> : null}
    </div>
  );
}

const inputClass = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const ArticlesConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [articleConfig, setArticleConfig] = useState(DEFAULT_ARTICLE_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/articles/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeArticlesConfig(raw || {});
      setArticleConfig(merged);
      syncArticlesConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading articles config:', error);
      const fallback = normalizeArticlesConfig({});
      setArticleConfig(fallback);
      syncArticlesConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeArticlesConfig(articleConfig);
      await localApiAdapter.put('/articles/config', payload);
      syncArticlesConfigToSettings(payload, patchSettings, settingsRef.current);
      setArticleConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving articles config:', error);
      setSaveError(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const resetConfig = normalizeArticlesConfig({});
      setArticleConfig(resetConfig);
      await localApiAdapter.put('/articles/config', resetConfig);
      syncArticlesConfigToSettings(resetConfig, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error resetting articles config:', error);
      alert(p('reset_error'));
    }
  };

  const updateConfig = (key, value) => {
    setArticleConfig((prev) => {
      const next = normalizeArticlesConfig({ ...prev, [key]: value });
      syncArticlesConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const productsTab = (
    <div className="space-y-6">
      <ConfigSection icon={Package} title={p('section_products')} hint={p('section_products_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('default_unit')} hint={p('default_unit_hint')}>
            <input
              type="text"
              value={articleConfig.defaultUnit}
              onChange={(e) => updateConfig('defaultUnit', e.target.value)}
              className={inputClass}
              placeholder={p('placeholder_default_unit')}
            />
          </ConfigField>
          <ConfigField label={p('default_tax_rate')} hint={p('default_tax_rate_hint', { rate: SAUDI_BRAND.vatRate })}>
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={articleConfig.defaultTaxRate}
              onChange={(e) => updateConfig('defaultTaxRate', parseInt(e.target.value, 10) || 0)}
              className={inputClass}
            />
          </ConfigField>
          <ConfigField label={p('product_reference_prefix')} hint={p('product_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.productReferencePrefix}
              onChange={(e) => updateConfig('productReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={articleConfig.autoGenerateProductReference}
              onChange={(v) => updateConfig('autoGenerateProductReference', v)}
              label={p('auto_generate_product_reference')}
            />
            <ConfigCheckbox
              checked={articleConfig.requireCategory}
              onChange={(v) => updateConfig('requireCategory', v)}
              label={p('require_category')}
            />
            <ConfigCheckbox
              checked={articleConfig.requireBarcode}
              onChange={(v) => updateConfig('requireBarcode', v)}
              label={p('require_barcode')}
            />
            <ConfigCheckbox
              checked={articleConfig.allowDuplicateBarcode}
              onChange={(v) => updateConfig('allowDuplicateBarcode', v)}
              label={p('allow_duplicate_barcode')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={Briefcase} title={p('section_services')} hint={p('section_services_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('service_reference_prefix')} hint={p('service_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.serviceReferencePrefix}
              onChange={(e) => updateConfig('serviceReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex items-center">
            <ConfigCheckbox
              checked={articleConfig.autoGenerateServiceReference}
              onChange={(v) => updateConfig('autoGenerateServiceReference', v)}
              label={p('auto_generate_service_reference')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const categoriesTab = (
    <div className="space-y-6">
      <ConfigSection icon={FolderOpen} title={p('section_categories')} hint={p('section_categories_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('category_reference_prefix')} hint={p('category_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.categoryReferencePrefix}
              onChange={(e) => updateConfig('categoryReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex items-center">
            <ConfigCheckbox
              checked={articleConfig.autoGenerateCategoryReference}
              onChange={(v) => updateConfig('autoGenerateCategoryReference', v)}
              label={p('auto_generate_category_reference')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={Columns} title={p('section_variants')} hint={p('section_variants_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('variant_reference_prefix')} hint={p('variant_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.variantReferencePrefix}
              onChange={(e) => updateConfig('variantReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={articleConfig.enableVariants}
              onChange={(v) => updateConfig('enableVariants', v)}
              label={p('enable_variants')}
            />
            <ConfigCheckbox
              checked={articleConfig.autoGenerateVariantReference}
              onChange={(v) => updateConfig('autoGenerateVariantReference', v)}
              label={p('auto_generate_variant_reference')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const unitsTab = (
    <ConfigSection icon={Scale} title={p('section_units')} hint={p('section_units_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('uom_reference_prefix')} hint={p('uom_reference_prefix_hint')}>
          <input
            type="text"
            value={articleConfig.uomReferencePrefix}
            onChange={(e) => updateConfig('uomReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <div className="flex items-center">
          <ConfigCheckbox
            checked={articleConfig.autoGenerateUomReference}
            onChange={(v) => updateConfig('autoGenerateUomReference', v)}
            label={p('auto_generate_uom_reference')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const pricingTab = (
    <div className="space-y-6">
      <ConfigSection icon={Calculator} title={p('section_cost_methods')} hint={p('section_cost_methods_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('cost_method_reference_prefix')} hint={p('cost_method_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.costMethodReferencePrefix}
              onChange={(e) => updateConfig('costMethodReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={articleConfig.enableCostMethods}
              onChange={(v) => updateConfig('enableCostMethods', v)}
              label={p('enable_cost_methods')}
            />
            <ConfigCheckbox
              checked={articleConfig.autoGenerateCostMethodReference}
              onChange={(v) => updateConfig('autoGenerateCostMethodReference', v)}
              label={p('auto_generate_cost_method_reference')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={Tags} title={p('section_price_lists')} hint={p('section_price_lists_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('price_list_reference_prefix')} hint={p('price_list_reference_prefix_hint')}>
            <input
              type="text"
              value={articleConfig.priceListReferencePrefix}
              onChange={(e) => updateConfig('priceListReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={articleConfig.enablePriceLists}
              onChange={(v) => updateConfig('enablePriceLists', v)}
              label={p('enable_price_lists')}
            />
            <ConfigCheckbox
              checked={articleConfig.autoGeneratePriceListReference}
              onChange={(v) => updateConfig('autoGeneratePriceListReference', v)}
              label={p('auto_generate_price_list_reference')}
            />
            <ConfigCheckbox
              checked={articleConfig.defaultPriceIncludesVat}
              onChange={(v) => updateConfig('defaultPriceIncludesVat', v)}
              label={p('default_price_includes_vat')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const auditTab = (
    <div className="space-y-6">
      <ConfigSection icon={History} title={p('section_changelog')} hint={p('section_changelog_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigCheckbox
            checked={articleConfig.enableChangelog}
            onChange={(v) => updateConfig('enableChangelog', v)}
            label={p('enable_changelog')}
          />
          <ConfigCheckbox
            checked={articleConfig.auditArticleChanges}
            onChange={(v) => updateConfig('auditArticleChanges', v)}
            label={p('audit_changes')}
          />
        </div>
      </ConfigSection>

      <ConfigSection icon={Eye} title={p('section_display')} hint={p('section_display_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('items_per_page')} hint={p('items_per_page_hint')}>
            <select
              value={articleConfig.itemsPerPage}
              onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))}
              className={inputClass}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n} className="text-gray-900">{n}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label={p('default_view')} hint={p('default_view_hint')}>
            <select
              value={articleConfig.defaultView}
              onChange={(e) => updateConfig('defaultView', e.target.value)}
              className={inputClass}
            >
              <option value="table" className="text-gray-900">{p('view_table')}</option>
              <option value="cards" className="text-gray-900">{p('view_cards')}</option>
            </select>
          </ConfigField>
        </div>
      </ConfigSection>
    </div>
  );

  const tabs = [
    { id: 'products', label: p('tab_products_services'), icon: Package },
    { id: 'categories', label: p('tab_categories_variants'), icon: FolderOpen },
    { id: 'units', label: p('tab_units'), icon: Scale },
    { id: 'pricing', label: p('tab_cost_pricing'), icon: Calculator },
    { id: 'audit', label: p('tab_audit_display'), icon: Shield },
  ];

  const tabContent = {
    products: productsTab,
    categories: categoriesTab,
    units: unitsTab,
    pricing: pricingTab,
    audit: auditTab,
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved_success')}
      saveError={saveError}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default ArticlesConfigPage;
